import {
  featureOf,
  frameStep,
  stationStandOff,
  yawTowards,
  type FramingScene,
  type Step,
  type View,
} from '@vrlearning/lesson-schema';

export type Vec3 = readonly [number, number, number];

export type PlacedObject = {
  readonly position: Vec3;
  /** For a `{station}` view's stand-off, which is kept exactly as it was. */
  readonly radius?: number | undefined;
  /** Outer ring radius in metres, when the object has rings; `{station}` only. */
  readonly ringOuter?: number | undefined;
};

export type SceneGeometry = {
  readonly objects: ReadonlyMap<string, PlacedObject>;
  /** Object that lights the scene, if any. Used to pick the lit side. */
  readonly lightSource?: string | undefined;
  /** Every object's bounds as the lesson declares them, for framing a step's subject. */
  readonly framing: FramingScene;
  /**
   * The scene as a step ends, after its shows, hides and moves: what that
   * step is framed by (lesson-schema's `sceneStateAt`). Without it, the
   * declared scene.
   */
  readonly framingFor?: (step: Step) => FramingScene;
};

export type ResolvedView = {
  /** Where the eyes are. */
  readonly position: Vec3;
  readonly lookAt: Vec3;
  /**
   * Set when the engine chose where to stand: the floor under the eyes. The
   * rig stands on the room's floor there, so a child's eyes are at a child's
   * height and the framing was computed for them.
   */
  readonly floor?: Vec3;
};

/**
 * The eye height explicit and station viewpoints are written for: the rig
 * stands at `pos.y − 1.6`, as it always has, so a lesson's `pos` is where an
 * adult's eyes are.
 */
export const WRITTEN_EYE = 1.6;

const isVec3 = (value: unknown): value is Vec3 =>
  Array.isArray(value) && value.length === 3 && value.every((n) => typeof n === 'number');

/**
 * Turns a lesson's `view` into a place to stand and a point to face.
 *
 * - `{look: id}` is framed (docs/superpowers/specs/2026-09-28-clear-view-design.md):
 *   the pupil stands where the step's subject -- that object and the step's
 *   `focus` -- fills a sensible part of the view above the lectern.
 * - `{pos, look}` is honoured to the centimetre: only the lectern may move.
 * - `{station}` keeps the old stand-off; only the unconverted campus tour uses it.
 *
 * An unresolvable reference returns undefined rather than throwing: a lesson
 * with one bad step should lose that step, not collapse in front of a class.
 * The schema's semantic validation is what stops such a lesson shipping.
 */
export function resolveView(
  view: View,
  geometry: SceneGeometry,
  options: {
    focus?: string | undefined;
    eye?: number | undefined;
    /** The scene as the step ends; the declared one by default. */
    scene?: FramingScene | undefined;
    /** The step's question, if any: its answers raise the lectern, and a framed view clears that. */
    questions?: Pick<Step, 'quiz' | 'predict'> | undefined;
    /** Where the pupil's eyes are now: a world's feature is faced from there. */
    from?: Vec3 | undefined;
  } = {},
): ResolvedView | undefined {
  const scene = options.scene ?? geometry.framing;
  /** Where a world's feature is (ADR-032): far off, faced, never walked to. */
  const feature = (look: unknown): Vec3 | undefined =>
    featureOf(look) === undefined ? undefined : scene.features?.get(look as string)?.round?.centre;
  if ('pos' in view && isVec3(view.pos)) {
    // Where the object is as the step ends: a move may have taken it elsewhere.
    const target = isVec3(view.look)
      ? view.look
      : (scene.states.get(view.look)?.pos ??
        geometry.objects.get(view.look)?.position ??
        feature(view.look));
    return target ? { position: view.pos, lookAt: target } : undefined;
  }

  if ('look' in view && featureOf(view.look) !== undefined) {
    const target = feature(view.look);
    if (!target) return undefined;
    const position = options.from ?? [0, options.eye ?? WRITTEN_EYE, 0];
    return { position, lookAt: target, floor: [position[0], 0, position[2]] };
  }

  // A big library model (ADR-043) is faced as a feature is: the pupil turns to
  // it from where they stand, never walked nearer (the distance rule).
  if ('look' in view && facesInPlace(view, scene)) {
    const at = scene.states.get(view.look)?.pos;
    if (!at) return undefined;
    const position = options.from ?? [0, options.eye ?? WRITTEN_EYE, 0];
    return { position, lookAt: [at[0], position[1], at[2]], floor: [position[0], 0, position[2]] };
  }

  if ('station' in view) {
    const object = geometry.objects.get(view.station);
    if (!object) return undefined;
    const light = geometry.lightSource
      ? geometry.objects.get(geometry.lightSource)?.position
      : undefined;
    const position = stationStandOff({
      position: object.position,
      radius: object.radius,
      ringOuter: object.ringOuter,
      light,
      eye: WRITTEN_EYE,
    });
    return { position, lookAt: object.position };
  }

  const framed = frameStep(
    {
      view,
      ...(options.focus !== undefined ? { focus: options.focus } : {}),
      ...(options.questions?.quiz ? { quiz: options.questions.quiz } : {}),
      ...(options.questions?.predict ? { predict: options.questions.predict } : {}),
    },
    scene,
    options.eye ?? WRITTEN_EYE,
  );
  return framed ? { position: framed.eye, lookAt: framed.lookAt, floor: framed.floor } : undefined;
}

/**
 * Whether a view only turns the pupil where they stand: `{look}` at a world's
 * feature (ADR-032) or at a big library model (ADR-043), as lesson-schema's
 * framing has it.
 */
export function facesInPlace(view: View, scene: FramingScene): view is View & { look: string } {
  if (!('look' in view) || 'pos' in view || typeof view.look !== 'string') return false;
  return featureOf(view.look) !== undefined || scene.models?.get(view.look)?.small === false;
}

/** Where the rig goes for a view, and its turn about y to face what it looks at. */
export function rigPose(view: ResolvedView): { position: Vec3; yaw: number } {
  const position: Vec3 = view.floor
    ? [view.floor[0], 0, view.floor[2]]
    : [view.position[0], view.position[1] - WRITTEN_EYE, view.position[2]];
  return { position, yaw: yawTowards(view.position, view.lookAt) };
}
